import { Vector3 } from "@babylonjs/core/Maths/math.vector";
class PhysicsInitializer {
    _wasmInternal;
    constructor(wasmInternal) {
        this._wasmInternal = wasmInternal;
    }
    add(model) {
        // this operation is thread safe
        this._wasmInternal.markMmdModelPhysicsAsNeedInit(model.ptr);
    }
}
/**
 * @internal
 */
export class MmdWasmPhysicsRuntime {
    nextWorldId;
    initializer;
    _mmdRuntime;
    _physicsWorldPtr;
    _maxSubSteps;
    _fixedTimeStep;
    _gravity;
    _worldMatrixBuffer;
    _impl;
    constructor(mmdRuntime) {
        this.nextWorldId = 0;
        this.initializer = new PhysicsInitializer(mmdRuntime.wasmInternal);
        this._mmdRuntime = mmdRuntime;
        this._physicsWorldPtr = mmdRuntime.wasmInternal.getMultiPhysicsWorld();
        this._maxSubSteps = 5;
        this._fixedTimeStep = 1 / 60;
        this._gravity = new Vector3(0, -98, 0);
        const worldMatrixBufferPtr = mmdRuntime.wasmInstance.allocateBuffer(16 * 4);
        this._worldMatrixBuffer = mmdRuntime.wasmInstance.createTypedArray(Float32Array, worldMatrixBufferPtr, 16);
        this._impl = null;
        this.setGravity(this._gravity);
    }
    dispose() {
        if (!this._worldMatrixBuffer) {
            return;
        }
        const worldMatrixBuffer = this._worldMatrixBuffer.array;
        this._mmdRuntime.wasmInstance.deallocateBuffer(worldMatrixBuffer.byteOffset, worldMatrixBuffer.byteLength);
        this._worldMatrixBuffer = null;
        this._impl?.dispose();
        this._impl = null;
    }
    /**
     * For multiple physics world methods
     */
    _nullCheck() {
        if (this._worldMatrixBuffer === null) {
            throw new Error("MmdWasmPhysicsRuntime is disposed");
        }
    }
    get maxSubSteps() {
        return this._maxSubSteps;
    }
    set maxSubSteps(value) {
        this._mmdRuntime.lock.wait();
        this._maxSubSteps = value;
        this._mmdRuntime.wasmInternal.setPhysicsMaxSubSteps(value);
    }
    get fixedTimeStep() {
        return this._fixedTimeStep;
    }
    set fixedTimeStep(value) {
        this._mmdRuntime.lock.wait();
        this._fixedTimeStep = value;
        this._mmdRuntime.wasmInternal.setPhysicsFixedTimeStep(value);
    }
    setGravity(gravity) {
        if (this._impl !== null) {
            this._impl.setGravity(gravity);
        }
        else {
            this._nullCheck();
            this._mmdRuntime.lock.wait();
            this._mmdRuntime.wasmInstance.multiPhysicsWorldSetGravity(this._physicsWorldPtr, gravity.x, gravity.y, gravity.z);
            this._gravity.copyFrom(gravity);
        }
    }
    getGravity(result) {
        if (this._impl !== null) {
            return this._impl.getGravityToRef(result ?? new Vector3());
        }
        else {
            result ??= new Vector3();
            return result.copyFrom(this._gravity);
        }
    }
    setMmdModelsWorldMatrix(mmdModels) {
        // set world matrix is thread safe because world matrix applied on before physics step
        const wasmInternal = this._mmdRuntime.wasmInternal;
        const worldMatrixBuffer = this._worldMatrixBuffer;
        for (let i = 0, len = mmdModels.length; i < len; ++i) {
            const mmdModel = mmdModels[i];
            const worldMatrixArray = worldMatrixBuffer.array;
            mmdModel.mesh.getWorldMatrix().copyToArray(worldMatrixArray, 0);
            wasmInternal.setMmdModelWorldMatrix(mmdModel.ptr, worldMatrixArray.byteOffset);
        }
    }
    getImpl(implCtor, options = {}) {
        if (this._impl === null) {
            this._impl = new implCtor(this._mmdRuntime, this._gravity, options);
        }
        return this._impl;
    }
    get impl() {
        return this._impl;
    }
}
